import { Color, MeshStandardMaterial, Vector3 } from 'three';

import modelLoaderService from './ModelLoaderService';

/**
 * The Flow pedestal prop, prepared for the board.
 *
 * Two things about the asset need answering before it can be lit.
 *
 * Its cyan trim is painted into the base colour with no emissive map, so however the
 * scene is lit it can never glow — nothing in a base colour is above 1.0. The first
 * pass answered that with an additive ring laid on the plate's top face, which read
 * as exactly what it was: a bright circle stuck onto an object rather than light
 * coming out of one. It is gone. The plate carries its colour as emission in its own
 * material instead, so the trim already modelled into the mesh is what lights up.
 *
 * Its brief also says the beam and halo are separate meshes to be deleted and
 * replaced. They are not, in this asset — see `plateTop`.
 */

export const PEDESTAL_URL = '/models/flow-pedestal-tripo.glb';

/**
 * The ground it covers, as a fraction of the cell it stands in.
 *
 * A plinth is placed by its footprint, not by its height: it has to sit inside its
 * own cell and how tall the model's proportions then make it is the model's
 * business. Sized by height instead — the natural thing to reach for, and what the
 * first pass did — the generator's proportions decide how much of the board a plinth
 * covers, and this one covered nearly three cells.
 *
 * Half a cell, not the sprite's three quarters. The sprite was a picture and could
 * sit right up against its cell edges; a real object with real thickness reads much
 * heavier at the same width, and a board of them at three quarters became a floor of
 * plinths with figures incidental on top. At a half the cell still shows around each
 * one, which is what keeps them furniture rather than terrain.
 */
export const PEDESTAL_FOOTPRINT = 0.45;

/**
 * How strongly the plate glows in its owner's colour, and what the status markers
 * add to it.
 *
 * This is the whole marker language now that the ring is gone. It has to stay low:
 * the plate is a lit metal object and the emission is a filament inside it, not a
 * lamp shining on the board. Past about a third the metal stops reading as metal.
 */
const RELATION_EMISSION = 0.22;

/**
 * The surface, and how much of it the terrain decides.
 *
 * All measured against the actual board rather than picked. The GLB ships a fully
 * rough dielectric, which is the one combination that can pick nothing up off an
 * environment; a plate meant to be machined metal needs the opposite, or its panel
 * detail never appears at any exposure.
 *
 * `TERRAIN_FLOOR` is what a plinth is worth standing on unlit ground — low, but not
 * zero, or a plate in shadow vanishes. `TERRAIN_GAIN` is how hard the floor drives
 * the rest. Together they put the plate inside the map's own value range: much
 * brighter and it reads as white plastic laid on the terrain, much darker and the
 * machining stops reading at all.
 */
const METALNESS = 0.95;
const ROUGHNESS = 0.28;
const TERRAIN_FLOOR = 0.05;
const TERRAIN_GAIN = 1.7;
const MARKER_GAIN = {
  none: 1,
  own: 1.25,
  hover: 1.7,
  selected: 2.1,
  targeted: 2.6,
};

const SLICES = 24;
const PLATE_WIDTH_FRACTION = 0.5;
const EMITTER_WIDTH_FRACTION = 0.15;

/**
 * The prop's silhouette, as the widest radius found in each of SLICES horizontal
 * bands. One pass over the geometry answers both "where does the plate end" and
 * "where does the emitter head end", and the two must be read off the same profile
 * or they can disagree about how tall the thing is.
 *
 * Expects an already-seated model: y up, base on zero.
 */
function sliceProfile(root) {
  const points = [];
  let low = Infinity;
  let high = -Infinity;

  root.updateWorldMatrix(false, true);
  root.traverse(object => {
    if (!object.isMesh || !object.geometry?.attributes?.position) return;
    const position = object.geometry.attributes.position;
    const point = new Vector3();
    for (let i = 0; i < position.count; i += 1) {
      point.fromBufferAttribute(position, i).applyMatrix4(object.matrixWorld);
      points.push(point.clone());
      if (point.y < low) low = point.y;
      if (point.y > high) high = point.y;
    }
  });
  if (!points.length || !(high > low)) return null;

  const radius = new Array(SLICES).fill(0);
  for (const point of points) {
    const slice = Math.min(
      SLICES - 1,
      Math.floor(((point.y - low) / (high - low)) * SLICES)
    );
    const r = Math.max(Math.abs(point.x), Math.abs(point.z));
    if (r > radius[slice]) radius[slice] = r;
  }

  return { low, high, radius, widest: Math.max(...radius) };
}

const sliceTop = (profile, slice) =>
  profile.low + ((slice + 1) / SLICES) * (profile.high - profile.low);

/**
 * How high the PLATE goes — the flat top a figure stands on, not the tip of the
 * finial above it.
 *
 * The brief says the beam and the halo are separate meshes that can be deleted by
 * name or by shape. In this asset they are not: the whole prop is one merged mesh of
 * 2 844 triangles, and nothing can be taken out of it. Its bounding box is therefore
 * twice the height of the plate, and anything derived from that box lands on thin
 * air.
 *
 * So the plate is measured out of the geometry instead. Sliced by height, the shape
 * says plainly where it ends: a near-constant radius from the floor up, then a
 * collapse to a third of it for the spire. The top is the last slice still carrying
 * the plate's own width.
 *
 * @returns the plate's top in seated units, or null if no plate can be read
 */
function plateTop(profile) {
  let top = profile.low;
  for (let slice = 0; slice < SLICES; slice += 1) {
    if (profile.radius[slice] < profile.widest * PLATE_WIDTH_FRACTION) break;
    top = sliceTop(profile, slice);
  }
  // Nothing plate-shaped at the bottom means this is not the shape we think it is;
  // say so rather than returning the floor.
  return top > profile.low ? top : null;
}

/**
 * How high the EMITTER goes — the top of the head that throws the projection, which
 * is where the power dot belongs.
 *
 * Not the plate, and not the bounding box either. The plate is the floor a figure
 * stands on and sits half way up the prop, so a dot placed there glows out of the
 * housing's waist rather than out of the lens. The box top is the tip of the light
 * spike above the lens, which is thin air.
 *
 * So the head is read off the same profile: scanning down from the top, the first
 * band still carrying real width is where the housing ends and the spike begins.
 *
 * @returns the emitter's top in seated units, or null if nothing but the spike is
 *          above the plate
 */
function emitterTop(profile) {
  for (let slice = SLICES - 1; slice >= 0; slice -= 1) {
    if (profile.radius[slice] >= profile.widest * EMITTER_WIDTH_FRACTION) {
      return sliceTop(profile, slice);
    }
  }
  return null;
}

/**
 * @returns a holder Group standing on y = 0, one cell's footprint wide, centred on
 *          x/z. Its position and scale are free for the caller to set.
 */
export async function buildPedestal() {
  const model = await modelLoaderService.load(PEDESTAL_URL);
  const holder = modelLoaderService.seat(model, PEDESTAL_FOOTPRINT);

  // Measured on the SEATED model, not the raw one. Seating is what puts the thing
  // y-up with its base on zero; the file's own axes are whatever the exporter felt
  // like, and slicing those by height measures the plate across instead of up.
  const profile = sliceProfile(holder);
  holder.userData.plateTop = (profile && plateTop(profile)) ?? holder.userData.height;
  holder.userData.emitterTop =
    (profile && emitterTop(profile)) ?? holder.userData.height;

  /*
   * The material is replaced rather than tweaked.
   *
   * The GLB ships a fully rough dielectric — metalness 0, roughness 0.9 — which is
   * the one combination that cannot pick anything up off an environment, and this
   * plate is meant to be machined metal whose whole surface detail comes from what
   * it reflects. Under the original settings it renders as a grey lump at any
   * exposure. Its own base-colour texture is kept; only the surface response and the
   * emission are ours.
   */
  const lit = [];
  holder.traverse(object => {
    if (!object.isMesh || !object.material) return;
    const source = object.material;
    const material = new MeshStandardMaterial({
      map: source.map || null,
      color: source.color ? source.color.clone() : new Color(0xffffff),
      metalness: METALNESS,
      roughness: ROUGHNESS,
      emissive: new Color(0x000000),
      emissiveMap: source.map || null,
      emissiveIntensity: 1,
    });
    object.material = material;
    lit.push(material);
  });

  holder.userData.lit = lit;
  return holder;
}

/**
 * Light a built pedestal for its owner and its surroundings.
 *
 * @param group   the holder from `buildPedestal`
 * @param color   the owner's relation colour, carried as the plate's own emission
 * @param marker  status the emission brightens for
 * @param ambient {r,g,b} in 0..1 of the ground it is standing on, or null
 */
const tint = new Color();

export function tintPedestal(group, color, marker = 'none', ambient = null) {
  const gain = MARKER_GAIN[marker] ?? 1;
  tint.set(color);

  (group.userData.lit || []).forEach(material => {
    material.emissive.copy(tint).multiplyScalar(RELATION_EMISSION * gain);

    /*
     * The floor's own colour, multiplied into the plate.
     *
     * This is what stops it reading as a decal. A prop lit identically wherever it
     * stands is lit by a room that is not the room it is in; picking the terrain up
     * means a plate in a dark corner goes dark and one under a sodium lamp goes
     * warm, exactly as everything painted into the map already does. Kept well off
     * black so a plinth never disappears into unlit ground.
     */
    if (ambient) {
      material.color.setRGB(
        TERRAIN_FLOOR + ambient.r * TERRAIN_GAIN,
        TERRAIN_FLOOR + ambient.g * TERRAIN_GAIN,
        TERRAIN_FLOOR + ambient.b * TERRAIN_GAIN
      );
    }
  });
}

export function disposePedestalGeometry() {
  modelLoaderService.dispose(PEDESTAL_URL);
}
